<template>
  <modal
    :open="open"
    target="#promptTarget"
    no-padding
    @update:open="updateOpen"
  >
    <div class="prompt-content">
      <div class="py-25 px-17.5">
        <div class="flex gap-x-2.5 items-center justify-center whitespace-nowrap mb-2.5">
          <div class="icon">
            <trash-icon v-if="variant === 'delete'" />
            <span v-else-if="variant === 'confirm'" class="font-bold text-2xl">?</span>
          </div>
          <h1>{{ variantText.title }}</h1>
        </div>
        <span class="whitespace-pre-wrap">{{ variantText.description }}</span>
      </div>
      <div class="buttons">
        <custom-button variant="outlined" @click="updateOpen(false)">
          {{ variantText.cancelText }}
        </custom-button>
        <custom-button variant="primary" @click="resolve">
          {{ variantText.resolveText }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import TrashIcon from '@images/trash.svg?component';
import type { PromptVariant } from '@utils/prompt/injects';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../Modal/Modal.vue';
import CustomButton from '../CustomButton.vue';

interface Props {
  open: boolean;
  variant: PromptVariant;
  message?: string;
}

const { t } = useI18n();

const emit = defineEmits(['update:open', 'resolve']);

const updateOpen = (value: boolean) => {
  if (!value) {
    emit('update:open', false);
  } else {
    emit('update:open', true);
  }
};

const resolve = () => emit('resolve');

const props = defineProps<Props>();

const variantText = computed(() =>
  (props.variant === 'delete'
    ? {
      title: t('prompt.deleteModal.title'),
      description: props.message ?? t('prompt.deleteModal.description'),
      cancelText: t('prompt.deleteModal.cancelButtonText'),
      resolveText: t('prompt.deleteModal.deleteButtonText'),
    }
    : {
      title: t('prompt.confirmModal.title'),
      description: props.message ?? t('prompt.confirmModal.defaultDescription'),
      cancelText: t('prompt.confirmModal.cancelButtonText'),
      resolveText: t('prompt.confirmModal.confirmButtonText'),
    }));
</script>

<style lang="scss" scoped>
.prompt-content {
  @apply relative;
  @apply text-center;
  @apply p-5;

  @include absolutePseudo('before') {
    @apply h-2.5 w-full left-0 top-0;
    @apply bg-primary;
  }
}

.buttons {
  @apply grid gap-x-5;
  grid-template-columns: 1fr 1fr;
}

.icon {
  @apply relative;
  @apply bg-primary rounded-full w-10 h-10;
  @apply flex items-center justify-center;

  flex: 0 0 auto;
  color: white;

  & > * {
    @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2;
    max-width: 80%;
    max-height: 80%;
  }
}
</style>
